import Editor from "@monaco-editor/react";
import { useEffect, useState } from "react";
import {
  emitChangeCode,
  CodeEditorOn,
  onCodeChange,
  saveCode,
} from "../sockets/socket";
import { useSelector } from "react-redux";
import { Code2, Lock, PenLine } from "lucide-react";

export default function CodeEditor({ roomId, isCodeEditorOpen, role, editorControlledBy }) {
  const [language, setLanguage] = useState("javascript");
  const [codeSnapshot, setCodeSnapShot] = useState("");
  const [lastSavedCode, setlastSavedCode] = useState("");
  const { codeEditorCode, codeLanguage } = useSelector(
    (state) => state.interview,
  );
  
  // Determine if editor should be read-only
  // Editor is editable only when role matches editorControlledBy
  const isReadOnly = role !== editorControlledBy;
  
  // Only update from socket if in read-only mode to avoid race conditions
  useEffect(() => {
    onCodeChange((code) => {
      // Only update if this user is in read-only mode
      // If they have edit control, ignore socket updates to prevent race conditions
      if (isReadOnly) {
        setCodeSnapShot(code)
      }
    });
  }, [role, isReadOnly]);
  
  useEffect(() => {
    if (codeLanguage) {
      setLanguage(codeLanguage);
    }
    if (codeEditorCode) {
      setCodeSnapShot(codeEditorCode);
      setlastSavedCode(codeEditorCode);
    }
  }, [codeEditorCode, codeLanguage]);
  const handleLanguageChange = (e) => {
    const newLanguage = e.target.value;
    setLanguage(newLanguage);
    saveCode({ roomId, codeSnapshot, language: newLanguage });
  };
  const handleEditorChange = (value) => {
    setCodeSnapShot(value);
    // Only emit changes if user has edit control
    if (!isReadOnly) {
      emitChangeCode({ roomId, codeSnapshot: value });
    }
  };
  useEffect(() => {
    if (codeSnapshot === lastSavedCode) return;

    const timer = setTimeout(() => {
      saveCodeSnapshot();
      setlastSavedCode(codeSnapshot);
    }, 5000);
    return () => clearTimeout(timer);
  }, [codeSnapshot, lastSavedCode]);

  const saveCodeSnapshot = () => {
    saveCode({ roomId, codeSnapshot, language });
  };

  useEffect(() => {
    CodeEditorOn(roomId);
  }, [isCodeEditorOpen, roomId]);

  return (
    <div className="flex w-full h-full flex-col overflow-hidden rounded-xl border border-white/10 bg-gray-900 shadow-modal">
      {/* Header with language selector */}
      <div className="flex items-center justify-between border-b border-white/10 bg-gray-900/90 px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-400/10 text-emerald-300 ring-1 ring-inset ring-emerald-400/20">
            <Code2 className="h-4 w-4" />
          </div>
          <h3 className="text-base font-semibold text-gray-100">Code Editor</h3>
          <span
            className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset ${
              isReadOnly
                ? "bg-red-500/10 text-red-300 ring-red-500/20"
                : "bg-emerald-500/10 text-emerald-300 ring-emerald-500/20"
            }`}
          >
            {isReadOnly ? (
              <Lock className="h-3 w-3" />
            ) : (
              <PenLine className="h-3 w-3" />
            )}
            {isReadOnly ? "Read-only" : "Editing"}
          </span>
        </div>

        <div className="flex items-center gap-2">
          <label
            htmlFor="language-select"
            className="hidden text-sm font-medium text-gray-400 sm:block"
          >
            Language
          </label>
          <select
            id="language-select"
            onChange={handleLanguageChange}
            value={language}
            className="cursor-pointer rounded-lg border border-white/10 bg-gray-800 px-3 py-1.5 text-sm font-medium text-gray-200 transition-colors hover:bg-gray-700 focus:ring-2 focus:ring-emerald-400/50 focus:outline-none"
          >
            <option value="javascript">JavaScript</option>
            <option value="python">Python</option>
            <option value="cpp">C++</option>
            <option value="java">Java</option>
            <option value="typescript">TypeScript</option>
            <option value="csharp">C#</option>
          </select>
        </div>
      </div>

      {/* Editor */}
      <div className="flex-1">
        <Editor
          height="60vh"
          language={language}
          value={codeSnapshot}
          theme="vs-dark"
          defaultValue="// Start coding here..."
          onChange={(value) => handleEditorChange(value)}
          options={{
            fontSize: 14,
            minimap: { enabled: true },
            scrollBeyondLastLine: false,
            wordWrap: "on",
            lineNumbers: "on",
            roundedSelection: true,
            cursorStyle: "line",
            automaticLayout: true,
            padding: { top: 16, bottom: 16 },
            suggestOnTriggerCharacters: true,
            quickSuggestions: true,
            wordBasedSuggestions: true,
            acceptSuggestionOnEnter: "on",
            tabSize: 2,
            renderLineHighlight: "all",
            scrollbar: {
              vertical: "auto",
              horizontal: "auto",
              useShadows: false,
            },
            readOnly: isReadOnly,
          }}
        />
      </div>
    </div>
  );
}
